import { Column, RowNode } from '@ag-grid-community/core'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from '@angular/core'
import { Account, IssueCounts } from '@helios/generic-components'
import { AgGridApi } from '@northfork/ag-grid'
import { IssuesType } from '../../account-list-grid/account-list-grid.component'
import {
  getIssueCountsForBreaksByLevel,
  mergeCounts,
} from '../../utils/break-counter'

interface IssueCountRow {
  category: string
  highFound: string
  highWaiting: string
  mediumFound: string
  mediumWaiting: string
}

@Component({
  selector: 'hs-issue-count-status',
  templateUrl: './issue-count-status.component.html',
  styleUrls: ['./issue-count-status.component.scss'],
  host: { class: 'hs-status-bar' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IssueCountStatusComponent {
  /** @internal **/
  allDataInMemory?: boolean
  private readonly highPriorityTotalName = 'High Priority Total'
  private readonly mediumPriorityTotalName = 'Medium Priority Total'
  readonly categories: string[] = [
    'Category',
    'High Found',
    'High Waiting',
    'Medium Found',
    'Medium Waiting',
  ]
  issueCountRows: IssueCountRow[] = []

  totalHighFound = '0'
  totalHighWaiting = '0'
  totalMediumFound = '0'
  totalMediumWaiting = '0'

  constructor(
    @Inject('AgGridApi') private readonly gridApi: AgGridApi,
    private readonly cd: ChangeDetectorRef,
  ) {}

  updateCounts() {
    const nodes = this.getAllNodes()
    const cols = this.getIssueColumns()
    const counts = this.getCountsFromColumns(cols, nodes)
    this.issueCountRows = this.countsToRowData(counts)
    this.updateTotalIssueCounts(counts)
    this.cd.detectChanges()
  }

  /**
   * @internal
   * @param counts
   */
  countsToRowData(counts: Record<string, IssueCounts>): IssueCountRow[] {
    const rows: IssueCountRow[] = []
    for (const key in counts) {
      const value = counts[key]
      const row: IssueCountRow = {
        category: key,
        highFound: this.sumToString(value.found?.high?.total),
        highWaiting: this.sumToString(value.waiting?.high?.total),
        mediumFound: this.sumToString(value.found?.medium?.total),
        mediumWaiting: this.sumToString(value.waiting?.medium?.total),
      }
      rows.push(row)
      if (key === this.highPriorityTotalName) {
        this.totalHighFound = row.highFound
        this.totalHighWaiting = row.highWaiting
      } else if (key === this.mediumPriorityTotalName) {
        this.totalMediumFound = row.mediumFound
        this.totalMediumWaiting = row.mediumWaiting
      }
    }
    return rows
  }

  private sumToString(sum: number | undefined): string {
    return `${sum ?? 0}${this.allDataInMemory ? '' : '+'}`
  }

  private getCountsFromColumns(
    cols: Column[],
    nodes: RowNode[],
  ): Record<string, IssueCounts> {
    const newCounts: Record<string, IssueCounts> = {}
    cols.forEach(col => {
      const issueCategory = col.getUserProvidedColDef()?.headerName
      if (issueCategory)
        newCounts[issueCategory] = this.getIssueCountSum(col, nodes)
    })
    return newCounts
  }

  private getIssueCountSum(col: Column, nodes: RowNode[]): IssueCounts {
    return mergeCounts(
      nodes
        .map(node => {
          if (node.data == null) {
            return undefined
          }
          if (col.getColDef().headerName === this.highPriorityTotalName) {
            return getIssueCountsForBreaksByLevel(node.data as Account, 'high')
          }
          if (col.getColDef().headerName === this.mediumPriorityTotalName) {
            return getIssueCountsForBreaksByLevel(
              node.data as Account,
              'medium',
            )
          }

          return node.data[col.getColId()] as IssueCounts | undefined
        })
        .filter((count): count is IssueCounts => count != null),
    )
  }

  private getIssueColumns(): Column[] {
    return (
      this.gridApi.columnApi
        .getAllColumns()
        ?.filter(col => col.getColDef().type === IssuesType) ?? []
    )
  }

  /** @internal */
  getAllNodes(): RowNode[] {
    const selectedRows = this.gridApi.api.getSelectedNodes()
    if (selectedRows.length > 0) {
      this.allDataInMemory = true
      return selectedRows
    }
    this.allDataInMemory =
      this.gridApi.api.getServerSideStoreState()[0]?.lastRowIndexKnown

    const nodes: RowNode[] = []
    this.gridApi.api.forEachNode(node => nodes.push(node))
    return nodes
  }

  updateTotalIssueCounts(counts: Record<string, IssueCounts>) {
    let totalHighFound = 0
    let totalHighWaiting = 0
    let totalMediumFound = 0
    let totalMediumWaiting = 0

    for (const key in counts) {
      totalHighFound += counts[key]?.found?.high?.total ?? 0
      totalHighWaiting += counts[key]?.waiting?.high?.total ?? 0
      totalMediumFound += counts[key]?.found?.medium?.total ?? 0
      totalMediumWaiting += counts[key]?.waiting?.medium?.total ?? 0
    }

    this.totalHighFound = this.sumToString(totalHighFound)
    this.totalHighWaiting = this.sumToString(totalHighWaiting)
    this.totalMediumFound = this.sumToString(totalMediumFound)
    this.totalMediumWaiting = this.sumToString(totalMediumWaiting)
  }
}
